/**
 * The live workshop flow for /admin/workshop: flow state, the facilitator's
 * controls (start, Seuraava/Edellinen, pause, stop) and the socket events that
 * keep them in sync across devices. Starting always runs the instance's module
 * chain (#682).
 */
import { useCallback, useEffect, useState } from 'react';
import SocketService from '../../../../packages/shared/socket';
import API_ENDPOINTS from '../config/api';

export default function useOrchestratorFlow(instanceId, { onChainChanged } = {}) {
  const [flowState, setFlowState] = useState(null);
  // Last flow-timer-state payload (#1153): { remaining, total, isPaused, … }.
  // Server-computed; controls tick it down locally between syncs.
  const [timer, setTimer] = useState(null);

  const loadFlowState = useCallback(async () => {
    if (!instanceId) return null;
    const res = await fetch(API_ENDPOINTS.FLOW_STATE(instanceId));
    const data = await res.json();
    const next = data.active ? data : null;
    setFlowState(next);
    return next;
  }, [instanceId]);

  useEffect(() => {
    if (!instanceId) return undefined;
    const socket = SocketService.connect(instanceId, 'facilitator', `orchestrator-${Date.now()}`);
    const handleConnect = () => {
      socket.emit('join-room', { room: `instance-${instanceId}` });
      socket.emit('flow-timer-state-request', { instanceId });
    };
    socket.on('connect', handleConnect);
    if (socket.connected) socket.emit('flow-timer-state-request', { instanceId });

    const handleStepChanged = (data) => {
      if (data.instanceId !== instanceId) return;
      setFlowState((prev) => (prev ? { ...prev, currentStep: data.step, stepStartedAt: new Date().toISOString(), elapsedMs: 0 } : prev));
    };
    const handleActivated = () => { loadFlowState(); onChainChanged?.(); };
    const handleDeactivated = () => { setFlowState(null); onChainChanged?.(); };
    const handlePaused = () => setFlowState((prev) => (prev ? { ...prev, isPaused: true } : prev));
    const handleResumed = () => setFlowState((prev) => (prev ? { ...prev, isPaused: false } : prev));
    const handleChainChanged = (data) => { if (!data?.instanceId || data.instanceId === instanceId) onChainChanged?.(); };
    const handleTimerState = (data) => { if (data?.instanceId === instanceId) setTimer(data); };

    socket.on('flow-timer-state', handleTimerState);
    socket.on('flow-step-changed', handleStepChanged);
    socket.on('flow-activated', handleActivated);
    socket.on('flow-deactivated', handleDeactivated);
    socket.on('flow-paused', handlePaused);
    socket.on('flow-resumed', handleResumed);
    socket.on('participant-chain-changed', handleChainChanged);
    return () => {
      socket.off('connect', handleConnect);
      socket.off('flow-timer-state', handleTimerState);
      socket.off('flow-step-changed', handleStepChanged);
      socket.off('flow-activated', handleActivated);
      socket.off('flow-deactivated', handleDeactivated);
      socket.off('flow-paused', handlePaused);
      socket.off('flow-resumed', handleResumed);
      socket.off('participant-chain-changed', handleChainChanged);
    };
  }, [instanceId, loadFlowState, onChainChanged]);

  const goToStep = useCallback(async (action, stepId = null) => {
    if (!instanceId) return;
    if (action === 'pause' || action === 'resume') {
      const res = await fetch(API_ENDPOINTS.FLOW_STEP(instanceId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      if (data.success) setFlowState((prev) => (prev ? { ...prev, isPaused: data.isPaused } : prev));
      return;
    }
    // next/previous walk screen states first, then modules; the tick engine applies the screens
    const res = await fetch(API_ENDPOINTS.FLOW_TICK(instanceId), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(action === 'goto' ? { stepId } : { direction: action }),
    });
    const data = await res.json();
    if (data.success && data.kind === 'step' && data.step) {
      setFlowState((prev) => (prev ? { ...prev, currentStep: data.step, stepStartedAt: new Date().toISOString(), elapsedMs: 0 } : prev));
    }
  }, [instanceId]);

  /** Start the workshop: the backend runs the chain from its first module. */
  const startWorkshop = useCallback(async () => {
    const res = await fetch(API_ENDPOINTS.FLOW_ACTIVATE(instanceId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) throw new Error(data.message || data.error || `HTTP ${res.status}`);
    await loadFlowState();
    onChainChanged?.();
  }, [instanceId, loadFlowState, onChainChanged]);

  const stopWorkshop = useCallback(async () => {
    await fetch(API_ENDPOINTS.FLOW_STATE(instanceId), { method: 'DELETE' });
    setFlowState(null);
  }, [instanceId]);

  const handleFlowAction = useCallback((action) => {
    if (action === 'stop') return stopWorkshop();
    return goToStep(action);
  }, [goToStep, stopWorkshop]);

  /** Extend (+) or shorten (−) the step timer by deltaMs (#1153, facilitator). */
  const adjustTimer = useCallback(async (deltaMs) => {
    if (!instanceId) return;
    await fetch(API_ENDPOINTS.FLOW_TIMER(instanceId), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ action: 'adjust', deltaMs }),
    });
    // The confirmed state arrives on flow-timer-state
  }, [instanceId]);

  return { flowState, timer, loadFlowState, goToStep, startWorkshop, stopWorkshop, handleFlowAction, adjustTimer };
}
